import type { CloseTabAction } from './tabCoordination.types';
import { getDetachedStorageKeys } from './storageKeys';

/**
 * The detached window this main tab opened, kept outside Redux
 *
 * Plain terms
 * -----------
 * The main tab has to notice when the reader closes the popped-out form, even
 * when the closing window never manages to say goodbye. Holding on to the
 * window it opened lets it simply look.
 *
 * Technical
 * ---------
 * A `Window` is not serializable, so it cannot live in the slice. `detachedAt`
 * marks the start of this detach session: a `tab-closed` record written to
 * localStorage before it belongs to an earlier session and is ignored.
 */
let detachedWindow: Window | null = null;
let detachedAt = 0;

export const rememberDetachedWindow = (win: Window) => {
  detachedWindow = win;
  detachedAt = Date.now();

  try {
    localStorage.removeItem(
      getDetachedStorageKeys(window.name).afterSubmitRedirect
    );
  } catch {
    // Storage can be unavailable; the flag then cannot be stale either.
  }
};

export const getDetachedWindow = () => detachedWindow;

export const isDetachedWindowClosed = () => Boolean(detachedWindow?.closed);

export const readDetachedClosePayload = (): CloseTabAction['payload'] => {
  const keys = getDetachedStorageKeys(window.name);

  const closed: CloseTabAction['payload'] = {
    role: 'spawn',
    status: 'closed',
    tabId: window.name,
    ts: Date.now(),
  };

  try {
    const stored = JSON.parse(localStorage.getItem(keys.closed) ?? 'null') as
      | CloseTabAction['payload']
      | null;

    if (
      stored?.role === 'spawn' &&
      stored.tabId === window.name &&
      stored.ts >= detachedAt
    ) {
      return stored;
    }

    const afterSubmit = localStorage.getItem(keys.afterSubmitRedirect) === '1';

    return afterSubmit
      ? { ...closed, reason: 'AFTER_SUBMIT_REDIRECT' }
      : closed;
  } catch {
    return closed;
  }
};
